import React from 'react';
import {
    TrendingDown,
    DollarSign,
    Cloud,
    ArrowRight,
    Globe,
    PieChart,
    BarChart3,
    Wallet,
    Leaf,
    Check
} from 'lucide-react';

/**
 * AI Economics Section - GPU ROI Maximization
 *
 * Key message: Make your GPU investment pay off.
 *
 * Showcases:
 * - Cost per token visibility
 * - Multi-cloud arbitrage
 * - Spot/preemptible utilization
 * - Overflow to serverless
 * - Real dollar savings
 */

// Cost optimization visual
const CostComparison = () => {
    return (
        <div className="relative p-8 rounded-3xl border border-white/[0.06] bg-white/[0.02]">
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent rounded-3xl" />

            <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl bg-volt-500 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-obsidian-900" />
                    </div>
                    <div>
                        <div className="text-white font-semibold">GPU Cost Optimization</div>
                        <div className="text-sm text-gray-500">Eliminate waste, maintain SLOs</div>
                    </div>
                </div>

                {/* Visual comparison bars */}
                <div className="space-y-6 mb-8">
                    {/* Before */}
                    <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-400">Typical over-provisioning</span>
                        </div>
                        <div className="h-12 rounded-lg bg-white/5 border border-white/10 relative overflow-hidden">
                            <div className="absolute inset-0 bg-white/10" style={{ width: '100%' }} />
                            <div className="absolute inset-y-0 left-0 flex items-center px-4">
                                <span className="text-xs text-gray-400 font-medium">40-60% idle capacity</span>
                            </div>
                        </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-center">
                        <ArrowRight className="w-6 h-6 text-volt-400 rotate-90" />
                    </div>

                    {/* After */}
                    <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-400">Forecaster-optimized</span>
                        </div>
                        <div className="h-12 rounded-lg bg-obsidian-700 border border-volt-500/30 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-volt-500/40 to-volt-400/40 rounded-l-lg" style={{ width: '55%' }} />
                            <div className="absolute inset-y-0 left-0 flex items-center px-4">
                                <span className="text-xs text-volt-400 font-medium">Right-sized + spot + multi-region routing</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* What we optimize */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-obsidian-800 text-center">
                        <div className="text-2xl font-bold text-volt-400">Spot</div>
                        <div className="text-xs text-gray-500">When SLO allows</div>
                    </div>
                    <div className="p-4 rounded-xl bg-obsidian-800 text-center">
                        <div className="text-2xl font-bold text-white">Scale</div>
                        <div className="text-xs text-gray-500">Predictive sizing</div>
                    </div>
                    <div className="p-4 rounded-xl bg-obsidian-800 text-center">
                        <div className="text-2xl font-bold text-white">Route</div>
                        <div className="text-xs text-gray-500">Cheapest provider</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Provider arbitrage visualization
const ProviderArbitrage = () => {
    const providers = [
        { name: 'AWS / GCP / Azure', status: 'primary' },
        { name: 'Nebius / CoreWeave', status: 'spot', best: true },
        { name: 'RunPod / Lambda Labs', status: 'multi-region' },
        { name: 'Modal / CloudRun', status: 'serverless' },
    ];

    return (
        <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-volt-500/10 flex items-center justify-center">
                    <Cloud className="w-4 h-4 text-volt-400" />
                </div>
                <div>
                    <div className="text-white font-medium">12 Cloud Providers</div>
                    <div className="text-xs text-gray-500">Intelligent routing across 30+ regions worldwide</div>
                </div>
            </div>

            <div className="space-y-2">
                {providers.map((provider, i) => (
                    <div
                        key={i}
                        className={`
                            flex items-center justify-between p-3 rounded-lg
                            ${provider.best ? 'bg-volt-500/10 border border-volt-500/30' : 'bg-white/[0.02]'}
                        `}
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-white">{provider.name}</span>
                            {provider.best && (
                                <span className="px-2 py-0.5 rounded text-xs bg-volt-500/20 text-volt-400">
                                    Best price
                                </span>
                            )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-gray-400">
                            {provider.status}
                        </span>
                    </div>
                ))}
            </div>

            <div className="mt-4 p-3 rounded-lg bg-volt-500/10 border border-volt-500/20">
                <div className="flex items-center gap-2 text-sm">
                    <TrendingDown className="w-4 h-4 text-volt-400" />
                    <span className="text-volt-400 font-medium">Multi-Region Intelligence across 12 providers</span>
                </div>
            </div>
        </div>
    );
};

// Feature cards
interface EconFeatureProps {
    icon: React.ElementType;
    title: string;
    description: string;
    metric: string;
    metricLabel: string;
}

const EconFeature = ({ icon: Icon, title, description }: Omit<EconFeatureProps, 'metric' | 'metricLabel'>) => (
    <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-volt-500/30 transition-colors">
        <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center mb-4">
            <Icon className="w-5 h-5 text-volt-400" />
        </div>
        <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
        <p className="text-sm text-gray-400">{description}</p>
    </div>
);

export const AIEconomicsSection = () => {
    const features = [
        {
            icon: PieChart,
            title: 'Cost per token visibility',
            description: 'Know exactly what each request costs. Break down by model, provider, and customer.',
        },
        {
            icon: BarChart3,
            title: 'Spot + on-demand mix',
            description: 'Automatically use spot when SLO allows, fallback to on-demand when interrupted.',
        },
        {
            icon: Globe,
            title: 'Multi-Region Intelligence',
            description: 'Burst traffic routes to the best region across 12 providers. Compliance-aware. Model weights follow your workloads.',
        },
        {
            icon: Wallet,
            title: 'Hibernate idle GPUs',
            description: 'Sleep GPUs when not in use, wake them in seconds. Stop paying for idle capacity.',
        },
        {
            icon: Leaf,
            title: 'Energy-optimized routing',
            description: 'Per-stage energy routing. Each inference stage runs on the most energy-efficient accelerator. Carbon-aware across 18+ grid regions.',
        },
    ];

    return (
        <section className="py-24 lg:py-32 bg-obsidian-900">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                        <TrendingDown className="w-4 h-4" />
                        AI Economics
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                        Make your GPU investment
                        <br />
                        <span className="text-gray-500">actually pay off.</span>
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Billions invested in AI infrastructure across every organization. Most of it sits idle.
                        Forecaster turns GPU spend into GPU ROI.
                    </p>
                </div>

                {/* Two column layout */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Left: Cost comparison */}
                    <CostComparison />

                    {/* Right: Provider arbitrage */}
                    <div className="space-y-6">
                        <ProviderArbitrage />
                    </div>
                </div>

                {/* Feature grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {features.map((feature, i) => (
                        <EconFeature key={i} {...feature} />
                    ))}
                </div>

                {/* Carbon-Aware Routing */}
                <div className="mt-16 p-8 rounded-2xl border border-volt-500/20 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent">
                    <div className="flex items-center gap-3 mb-2">
                        <Leaf className="w-6 h-6 text-volt-400" />
                        <h3 className="text-2xl font-bold text-white">Carbon-Aware Routing</h3>
                        <span className="px-2 py-0.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-xs text-volt-400">Built-in</span>
                    </div>
                    <p className="text-gray-400 mb-8 max-w-2xl">
                        Every inference request is scored across cost, energy, and carbon. Forecaster routes to the cleanest
                        available capacity while respecting your latency SLOs.
                    </p>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Grid carbon intensity */}
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Live Grid Intensity</div>
                            <div className="space-y-2">
                                {[
                                    { region: 'Sweden', co2: '20', clean: true },
                                    { region: 'Finland', co2: '25', clean: true },
                                    { region: 'Oregon (US)', co2: '100', clean: true },
                                    { region: 'Virginia (US)', co2: '380', clean: false },
                                    { region: 'Singapore', co2: '510', clean: false },
                                ].map((r, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">{r.region}</span>
                                        <span className={`text-sm font-medium ${r.clean ? 'text-emerald-400' : 'text-gray-500'}`}>
                                            {r.co2} gCO2/kWh
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-600 mt-3">18+ grid regions across AWS, GCP, Azure</p>
                        </div>

                        {/* Multi-objective scoring */}
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Routing Objective</div>
                            <div className="space-y-4">
                                {[
                                    { label: 'Energy efficiency', weight: '50%', width: '50%' },
                                    { label: 'Monetary cost', weight: '30%', width: '30%' },
                                    { label: 'Carbon emissions', weight: '20%', width: '20%' },
                                ].map((obj, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-400">{obj.label}</span>
                                            <span className="text-white font-medium">{obj.weight}</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-white/5">
                                            <div className="h-full rounded-full bg-volt-500/60" style={{ width: obj.width }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-600 mt-3">Weights configurable per policy</p>
                        </div>

                        {/* Per-request tracking */}
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Per-Request Metrics</div>
                            <div className="space-y-4">
                                <div className="p-3 rounded-lg bg-white/[0.03]">
                                    <div className="text-2xl font-bold text-volt-400">0.42g</div>
                                    <div className="text-xs text-gray-500">CO2 per inference</div>
                                </div>
                                <div className="p-3 rounded-lg bg-white/[0.03]">
                                    <div className="text-2xl font-bold text-white">847</div>
                                    <div className="text-xs text-gray-500">Tokens per Joule</div>
                                </div>
                                <div className="p-3 rounded-lg bg-white/[0.03]">
                                    <div className="text-2xl font-bold text-white">12.4J</div>
                                    <div className="text-xs text-gray-500">Energy per request</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Multi-Region Intelligence */}
                <div className="mt-16 p-8 rounded-2xl border border-volt-500/20 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent">
                    <div className="flex items-center gap-3 mb-2">
                        <Globe className="w-6 h-6 text-volt-400" />
                        <h3 className="text-2xl font-bold text-white">Multi-Region Intelligence</h3>
                    </div>
                    <p className="text-gray-400 mb-8 max-w-2xl">
                        Every overflow request is scored across region capacity, compliance requirements, and model locality.
                        Forecaster routes to the optimal region — not just the cheapest provider.
                    </p>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Compliance Zones</div>
                            <div className="space-y-2">
                                {['EU (GDPR)', 'US (SOC2)', 'HIPAA'].map(zone => (
                                    <div key={zone} className="flex items-center gap-2">
                                        <Check className="w-3 h-3 text-volt-400" />
                                        <span className="text-sm text-gray-300">{zone}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-600 mt-3">Workloads never leave declared zones</p>
                        </div>
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Region Selection</div>
                            <div className="space-y-3">
                                <div className="p-3 rounded-lg bg-white/[0.03]">
                                    <div className="text-2xl font-bold text-volt-400">30+</div>
                                    <div className="text-xs text-gray-500">Regions queried per request</div>
                                </div>
                                <div className="p-3 rounded-lg bg-white/[0.03]">
                                    <div className="text-2xl font-bold text-white">12</div>
                                    <div className="text-xs text-gray-500">Providers with region intelligence</div>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 rounded-xl bg-obsidian-800">
                            <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-4">Model Locality</div>
                            <p className="text-sm text-gray-400 mb-3">
                                Model weights follow your workloads. Same model routes to the same provider + region — weights already cached, zero cold start penalty.
                            </p>
                            <div className="p-3 rounded-lg bg-white/[0.03]">
                                <div className="text-2xl font-bold text-volt-400">~0s</div>
                                <div className="text-xs text-gray-500">Weight loading on cache hit</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CFO CapEx Pitch */}
                <div className="mt-16 p-8 rounded-2xl border border-volt-500/20 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent">
                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-white mb-2">Same CapEx. 3x the Revenue.</h3>
                        <p className="text-gray-400 max-w-2xl mx-auto">
                            The $700B question isn't whether to spend on GPUs — it's whether that spend is productive.
                            Most GPU fleets run at 15-30% utilization. That's 70% of CapEx generating zero revenue.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4">
                        <div className="p-5 rounded-xl bg-obsidian-800 text-center">
                            <p className="text-3xl font-bold text-volt-400 mb-1">30% → 87%</p>
                            <p className="text-xs text-gray-500">GPU utilization</p>
                        </div>
                        <div className="p-5 rounded-xl bg-obsidian-800 text-center">
                            <p className="text-3xl font-bold text-white mb-1">847</p>
                            <p className="text-xs text-gray-500">Tokens per Joule</p>
                        </div>
                        <div className="p-5 rounded-xl bg-obsidian-800 text-center">
                            <p className="text-3xl font-bold text-white mb-1">Dozens</p>
                            <p className="text-xs text-gray-500">Concurrent workloads per GPU</p>
                        </div>
                        <div className="p-5 rounded-xl bg-obsidian-800 text-center">
                            <p className="text-3xl font-bold text-white mb-1">Ahead</p>
                            <p className="text-xs text-gray-500">Predictive, not reactive scaling</p>
                        </div>
                    </div>

                    <p className="text-center text-sm text-gray-500 mt-6">
                        Your $100M GPU investment at 30% utilization? Forecaster makes it perform like $300M.
                    </p>
                </div>

                {/* Bottom insight */}
                <div className="mt-16 text-center">
                    <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                        <span className="text-white font-semibold">Compute is revenue.</span>{' '}
                        Without compute, there's no way to generate tokens.
                        Forecaster makes sure every GPU second produces revenue-generating tokens, not idle heat.
                    </p>
                </div>

                {/* Section divider */}
                <div className="max-w-xl mx-auto mt-24">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
            </div>
        </section>
    );
};
